import { useMemo } from 'react';
import { useFiltersStore, PriceAlertChangeType } from '../stores/appFiltersStore';

export interface PriceAlertFilters {
  platforms: string[];
  sections: string[];
  categories: string[];
  changeType: PriceAlertChangeType;
}

export interface UsePriceAlertFiltersConfig {
  includePlatforms?: boolean;
  includeSections?: boolean;
  includeCategories?: boolean;
}

export interface UsePriceAlertFiltersReturn {
  filters: PriceAlertFilters;
  setPlatformFilters?: (filters: string[]) => void;
  setSectionFilters?: (filters: string[]) => void;
  setCategoryFilters?: (filters: string[]) => void;
  setChangeType: (type: PriceAlertChangeType) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

/**
 * Hook for managing price alert filters
 * Returns current filter state and setters for updating filters
 * @param config - Optional configuration to include specific filters
 */
export function usePriceAlertFilters(config: UsePriceAlertFiltersConfig = {
  includePlatforms: true,
  includeSections: false,
  includeCategories: true,
}): UsePriceAlertFiltersReturn {
  const {
    includePlatforms = true,
    includeSections = false,
    includeCategories = true,
  } = config;

  const priceAlertPlatformFilters = useFiltersStore(s => s.priceAlertPlatformFilters);
  const setPriceAlertPlatformFilters = useFiltersStore(s => s.setPriceAlertPlatformFilters);
  const sectionFilters = useFiltersStore(s => s.sectionFilters); // Shared with order history
  const setSectionFilters = useFiltersStore(s => s.setSectionFilters); // Shared with order history
  const priceAlertCategoryFilters = useFiltersStore(s => s.priceAlertCategoryFilters);
  const setPriceAlertCategoryFilters = useFiltersStore(s => s.setPriceAlertCategoryFilters);
  const priceAlertChangeType = useFiltersStore(s => s.priceAlertChangeType);
  const setPriceAlertChangeType = useFiltersStore(s => s.setPriceAlertChangeType);
  const resetPriceAlertFilters = useFiltersStore(s => s.resetPriceAlertFilters);

  const filters: PriceAlertFilters = useMemo(() => ({
    platforms: includePlatforms ? priceAlertPlatformFilters : [],
    sections: includeSections ? sectionFilters : [],
    categories: includeCategories ? priceAlertCategoryFilters : [],
    changeType: priceAlertChangeType,
  }), [priceAlertPlatformFilters, sectionFilters, priceAlertCategoryFilters, priceAlertChangeType, includePlatforms, includeSections, includeCategories]);

  const hasActiveFilters = useMemo(() => {
    return (includePlatforms && priceAlertPlatformFilters.length > 0) ||
           (includeSections && sectionFilters.length > 0) ||
           (includeCategories && priceAlertCategoryFilters.length > 0) ||
           priceAlertChangeType !== 'all';
  }, [priceAlertPlatformFilters, sectionFilters, priceAlertCategoryFilters, priceAlertChangeType, includePlatforms, includeSections, includeCategories]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (includePlatforms && priceAlertPlatformFilters.length > 0) count++;
    if (includeSections && sectionFilters.length > 0) count++;
    if (includeCategories && priceAlertCategoryFilters.length > 0) count++;
    if (priceAlertChangeType !== 'all') count++;
    return count;
  }, [priceAlertPlatformFilters, sectionFilters, priceAlertCategoryFilters, priceAlertChangeType, includePlatforms, includeSections, includeCategories]);

  return {
    filters,
    setPlatformFilters: includePlatforms ? setPriceAlertPlatformFilters : undefined,
    setSectionFilters: includeSections ? setSectionFilters : undefined,
    setCategoryFilters: includeCategories ? setPriceAlertCategoryFilters : undefined,
    setChangeType: setPriceAlertChangeType,
    resetFilters: resetPriceAlertFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}
